import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { X } from "lucide-react";

import { ApiError, api } from "../../api/client";
import type {
  BreakPolicy,
  BreakRule,
  BreakType,
  Department,
  Region,
  Site,
} from "../../types";
import {
  formatBreakPolicy,
  formatBreakType,
  formatMinutes,
} from "../../utils/formatters";
import DropdownSelect from "../../components/DropdownSelect";

// TODO: translate admin panel once Phase 2 ships broader i18n coverage.

export default function SiteSettings() {
  const [regions, setRegions] = useState<Region[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [departmentsBySite, setDepartmentsBySite] = useState<
    Record<number, Department[]>
  >({});
  const [expandedSiteId, setExpandedSiteId] = useState<number | null>(null);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [creatingForSiteId, setCreatingForSiteId] = useState<number | null>(null);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [creatingSite, setCreatingSite] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteSite = async (site: Site) => {
    if (!confirm(`Delete ${site.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/sites/${site.id}`);
      void loadAll();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to delete site");
      }
    }
  };

  const loadAll = useCallback(async () => {
    try {
      const [regionList, siteList] = await Promise.all([
        api.get<Region[]>("/api/regions"),
        api.get<Site[]>("/api/sites"),
      ]);
      setRegions(regionList);
      setSites(siteList);

      const depts: Record<number, Department[]> = {};
      await Promise.all(
        siteList.map(async (site) => {
          const list = await api.get<Department[]>(
            `/api/departments?site_id=${site.id}&include_inactive=true`,
          );
          depts[site.id] = list;
        }),
      );
      setDepartmentsBySite(depts);
      setError(null);
    } catch (err) {
      setError((err as Error).message || "Failed to load site settings");
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const sitesByRegion = useMemo(() => {
    const map: Record<number, Site[]> = {};
    for (const s of sites) {
      (map[s.region_id] ??= []).push(s);
    }
    return map;
  }, [sites]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Sites &amp; Departments</h1>
          <p className="text-sm text-gray-500">
            Configure working hours, break policies, and break rules per department.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreatingSite(true)}
          className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white"
        >
          + Add Site
        </button>
      </div>

      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}

      {regions.map((region) => (
        <section key={region.id}>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            {region.name}
          </h2>
          <div className="space-y-3">
            {(sitesByRegion[region.id] ?? []).map((site) => {
              const depts = departmentsBySite[site.id] ?? [];
              const isOpen = expandedSiteId === site.id;
              return (
                <div
                  key={site.id}
                  className="rounded-xl border border-gray-200 bg-white"
                >
                  <div className="flex w-full items-center justify-between px-5 py-4">
                    <button
                      type="button"
                      onClick={() => setExpandedSiteId(isOpen ? null : site.id)}
                      className="flex flex-1 items-center justify-between text-left"
                    >
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {site.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {site.timezone}
                          {site.address && <> · {site.address}</>}
                          {" · "}
                          {depts.filter((d) => d.is_active).length} active dept{depts.filter((d) => d.is_active).length !== 1 ? "s" : ""}
                          {site.is_kiosk_enabled && (
                            <span className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-blue-700">
                              Kiosk
                            </span>
                          )}
                        </p>
                      </div>
                      <span aria-hidden className="text-gray-400">
                        {isOpen ? "▾" : "▸"}
                      </span>
                    </button>
                    <div className="ml-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingSite(site)}
                        className="rounded-lg border border-gray-300 px-3 py-1 text-xs hover:bg-gray-50"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => void deleteSite(site)}
                        className="rounded-lg border border-red-300 px-3 py-1 text-xs text-red-600 hover:bg-red-50"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="border-t border-gray-100 px-5 py-4 space-y-4">
                      <KioskSection site={site} onSaved={() => void loadAll()} />
                      <div className="space-y-3">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Departments
                        </h3>
                        {depts.length === 0 && (
                          <p className="text-sm text-gray-400">
                            No departments for this site yet.
                          </p>
                        )}
                        {depts.map((d) => (
                          <DepartmentRow
                            key={d.id}
                            department={d}
                            onEdit={() => setEditingDepartment(d)}
                          />
                        ))}
                        <button
                          type="button"
                          onClick={() => setCreatingForSiteId(site.id)}
                          className="mt-1 rounded-lg bg-pme-blue px-3 py-2 text-xs font-semibold text-white"
                        >
                          + Add Department
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}

      {editingDepartment && (
        <DepartmentModal
          department={editingDepartment}
          onClose={() => setEditingDepartment(null)}
          onSaved={() => {
            setEditingDepartment(null);
            void loadAll();
          }}
        />
      )}
      {creatingForSiteId != null && (
        <DepartmentModal
          siteId={creatingForSiteId}
          onClose={() => setCreatingForSiteId(null)}
          onSaved={() => {
            setCreatingForSiteId(null);
            void loadAll();
          }}
        />
      )}
      {editingSite && (
        <SiteModal
          site={editingSite}
          regions={regions}
          onClose={() => setEditingSite(null)}
          onSaved={() => {
            setEditingSite(null);
            void loadAll();
          }}
        />
      )}
      {creatingSite && (
        <SiteModal
          site={null}
          regions={regions}
          onClose={() => setCreatingSite(false)}
          onSaved={() => {
            setCreatingSite(false);
            void loadAll();
          }}
        />
      )}
    </div>
  );
}

// ---- Site modal -------------------------------------------------------------

type SiteFormState = {
  name: string;
  region_id: number | "";
  timezone: string;
  address: string;
  geofence_lat: string;
  geofence_lng: string;
  geofence_radius_m: string;
  is_kiosk_site: boolean;
  is_kiosk_enabled: boolean;
  kiosk_session_minutes: number;
};

function SiteModal({
  site,
  regions,
  onClose,
  onSaved,
}: {
  site: Site | null;
  regions: Region[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = site !== null;

  const defaultRegion = regions.length > 0 ? regions[0] : null;

  const [form, setForm] = useState<SiteFormState>(() => {
    if (site) {
      return {
        name: site.name,
        region_id: site.region_id,
        timezone: site.timezone,
        address: site.address ?? "",
        geofence_lat: site.geofence_lat != null ? String(site.geofence_lat) : "",
        geofence_lng: site.geofence_lng != null ? String(site.geofence_lng) : "",
        geofence_radius_m: site.geofence_radius_m != null ? String(site.geofence_radius_m) : "",
        is_kiosk_site: site.is_kiosk_site,
        is_kiosk_enabled: site.is_kiosk_enabled,
        kiosk_session_minutes: site.kiosk_session_minutes,
      };
    }
    return {
      name: "",
      region_id: defaultRegion?.id ?? "",
      timezone: defaultRegion?.timezone_default ?? "",
      address: "",
      geofence_lat: "",
      geofence_lng: "",
      geofence_radius_m: "",
      is_kiosk_site: false,
      is_kiosk_enabled: false,
      kiosk_session_minutes: 15,
    };
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = <K extends keyof SiteFormState>(key: K, value: SiteFormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleRegionChange = (regionId: number) => {
    const region = regions.find((r) => r.id === regionId);
    update("region_id", regionId);
    if (region) {
      update("timezone", region.timezone_default);
    }
  };

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    if (form.region_id === "") return;

    setSubmitting(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        name: form.name.trim(),
        region_id: form.region_id,
        timezone: form.timezone || null,
        address: form.address || null,
        geofence_lat: form.geofence_lat !== "" ? Number(form.geofence_lat) : null,
        geofence_lng: form.geofence_lng !== "" ? Number(form.geofence_lng) : null,
        geofence_radius_m: form.geofence_radius_m !== "" ? Number(form.geofence_radius_m) : null,
        is_kiosk_site: form.is_kiosk_site,
        is_kiosk_enabled: form.is_kiosk_enabled,
        kiosk_session_minutes: form.kiosk_session_minutes,
      };

      if (isEdit) {
        await api.put(`/api/sites/${site.id}`, payload);
      } else {
        await api.post("/api/sites", payload);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 px-4 py-10"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl space-y-4 rounded-xl bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-gray-100 pb-3">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEdit ? `Edit ${site.name}` : "New Site"}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400">
            <X size={16} strokeWidth={2} aria-hidden />
          </button>
        </header>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <form onSubmit={submit} className="space-y-3 text-sm">
          <Row>
            <Field label="Site Name">
              <input
                required
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className="input"
              />
            </Field>
            <Field label="Region">
              <select
                required
                value={form.region_id}
                onChange={(e) => handleRegionChange(Number(e.target.value))}
                className="input"
              >
                <option value="" disabled>
                  Select a region
                </option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </Field>
          </Row>
          <Row>
            <Field label="Timezone">
              <input
                value={form.timezone}
                onChange={(e) => update("timezone", e.target.value)}
                className="input"
                placeholder="e.g. Europe/London"
              />
            </Field>
            <Field label="Address">
              <input
                value={form.address}
                onChange={(e) => update("address", e.target.value)}
                className="input"
                placeholder="Optional"
              />
            </Field>
          </Row>

          <fieldset className="rounded-lg border border-gray-200 p-3">
            <legend className="px-1 text-xs font-medium text-gray-500">
              Geofence (optional)
            </legend>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <Field label="Latitude">
                <input
                  type="number"
                  step="any"
                  value={form.geofence_lat}
                  onChange={(e) => update("geofence_lat", e.target.value)}
                  className="input"
                  placeholder="e.g. 51.5074"
                />
              </Field>
              <Field label="Longitude">
                <input
                  type="number"
                  step="any"
                  value={form.geofence_lng}
                  onChange={(e) => update("geofence_lng", e.target.value)}
                  className="input"
                  placeholder="e.g. -0.1278"
                />
              </Field>
              <Field label="Radius (metres)">
                <input
                  type="number"
                  min={0}
                  value={form.geofence_radius_m}
                  onChange={(e) => update("geofence_radius_m", e.target.value)}
                  className="input"
                  placeholder="e.g. 100"
                />
              </Field>
            </div>
          </fieldset>

          <fieldset className="rounded-lg border border-gray-200 p-3">
            <legend className="px-1 text-xs font-medium text-gray-500">
              Kiosk Settings
            </legend>
            <div className="flex flex-wrap items-end gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_kiosk_site}
                  onChange={(e) => update("is_kiosk_site", e.target.checked)}
                />
                <span>Is Kiosk Site</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_kiosk_enabled}
                  onChange={(e) => update("is_kiosk_enabled", e.target.checked)}
                />
                <span>Kiosk Enabled</span>
              </label>
              <Field label="Session Minutes">
                <input
                  type="number"
                  min={1}
                  max={480}
                  value={form.kiosk_session_minutes}
                  onChange={(e) => update("kiosk_session_minutes", Number(e.target.value))}
                  className="input w-24"
                />
              </Field>
            </div>
          </fieldset>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-pme-blue px-5 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {submitting ? "Saving..." : isEdit ? "Save" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---- Kiosk section ----------------------------------------------------------

function KioskSection({
  site,
  onSaved,
}: {
  site: Site;
  onSaved: () => void;
}) {
  const [enabled, setEnabled] = useState(site.is_kiosk_enabled);
  const [minutes, setMinutes] = useState(site.kiosk_session_minutes);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEnabled(site.is_kiosk_enabled);
    setMinutes(site.kiosk_session_minutes);
  }, [site.id, site.is_kiosk_enabled, site.kiosk_session_minutes]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/api/sites/${site.id}`, {
        is_kiosk_enabled: enabled,
        kiosk_session_minutes: minutes,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Kiosk Mode
      </h3>
      <p className="mt-1 text-xs text-gray-500">
        When enabled, a shared device at this site can be used as a clock-in
        kiosk. Employees enter only their PIN — no employee ID or passkey.
        PINs are enforced unique across all employees.
      </p>
      <div className="mt-3 flex flex-wrap items-end gap-3 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <span>Enable Kiosk Mode</span>
        </label>
        <label className="text-xs text-gray-600">
          Session timeout (minutes)
          <input
            type="number"
            min={1}
            max={480}
            value={minutes}
            onChange={(e) => setMinutes(Number(e.target.value))}
            className="ml-2 w-20 rounded border border-gray-300 px-2 py-1 text-sm"
          />
        </label>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
        >
          {saving ? "Saving..." : "Save Kiosk Settings"}
        </button>
        {error && <span className="text-xs text-red-600">{error}</span>}
      </div>
    </div>
  );
}


function DepartmentRow({
  department,
  onEdit,
}: {
  department: Department;
  onEdit: () => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2">
      <div>
        <p className="text-sm font-medium text-gray-900">
          {department.name}
          {!department.is_active && (
            <span className="ml-2 rounded bg-gray-200 px-1 text-[10px] uppercase text-gray-500">
              inactive
            </span>
          )}
        </p>
        <p className="text-xs text-gray-500">
          {formatBreakPolicy(department.break_policy)}
          {department.standard_start_time && department.standard_end_time && (
            <>
              {" · "}
              {department.standard_start_time.slice(0, 5)}–
              {department.standard_end_time.slice(0, 5)}
            </>
          )}
          {department.contracted_hours_daily && (
            <> · {department.contracted_hours_daily}h/day</>
          )}
        </p>
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="rounded-lg border border-gray-300 px-3 py-1 text-xs"
      >
        Edit
      </button>
    </div>
  );
}

// ---- Department modal ------------------------------------------------------

type DepartmentFormState = Partial<Department>;

function DepartmentModal({
  department,
  siteId,
  onClose,
  onSaved,
}: {
  department?: Department;
  siteId?: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!department;
  const [form, setForm] = useState<DepartmentFormState>(
    department ?? {
      site_id: siteId,
      name: "",
      break_policy: "manual",
      is_active: true,
    },
  );
  const [breakRules, setBreakRules] = useState<BreakRule[]>([]);
  const [addingRule, setAddingRule] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadRules = useCallback(async () => {
    if (!department) return;
    try {
      const list = await api.get<BreakRule[]>(
        `/api/departments/${department.id}/break-rules`,
      );
      setBreakRules(list);
    } catch {
      setBreakRules([]);
    }
  }, [department]);

  useEffect(() => {
    void loadRules();
  }, [loadRules]);

  const update = <K extends keyof DepartmentFormState>(
    key: K,
    value: DepartmentFormState[K],
  ) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = { ...form };
      if (isEdit && department) {
        await api.put(`/api/departments/${department.id}`, payload);
      } else {
        await api.post(`/api/departments`, payload);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const deactivate = async () => {
    if (!department) return;
    if (!confirm("Deactivate this department?")) return;
    await api.delete(`/api/departments/${department.id}`);
    onSaved();
  };

  const policy = (form.break_policy ?? "manual") as BreakPolicy;

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 px-4 py-10"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl space-y-4 rounded-xl bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-gray-100 pb-3">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEdit ? `Edit ${department?.name}` : "New department"}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400">
            <X size={16} strokeWidth={2} aria-hidden />
          </button>
        </header>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <form onSubmit={submit} className="space-y-3 text-sm">
          <Row>
            <Field label="Name">
              <input
                required
                value={form.name ?? ""}
                onChange={(e) => update("name", e.target.value)}
                className="input"
              />
            </Field>
            <Field label="Active?">
              <select
                value={form.is_active ? "true" : "false"}
                onChange={(e) =>
                  update("is_active", e.target.value === "true")
                }
                className="input"
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </Field>
          </Row>
          <Row>
            <Field label="Start time">
              <input
                type="time"
                value={(form.standard_start_time ?? "").slice(0, 5)}
                onChange={(e) =>
                  update(
                    "standard_start_time",
                    e.target.value === "" ? null : `${e.target.value}:00`,
                  )
                }
                className="input"
              />
            </Field>
            <Field label="End time">
              <input
                type="time"
                value={(form.standard_end_time ?? "").slice(0, 5)}
                onChange={(e) =>
                  update(
                    "standard_end_time",
                    e.target.value === "" ? null : `${e.target.value}:00`,
                  )
                }
                className="input"
              />
            </Field>
          </Row>
          <Row>
            <Field label="Contracted hours / day">
              <input
                type="number"
                step="0.5"
                value={form.contracted_hours_daily ?? ""}
                onChange={(e) =>
                  update(
                    "contracted_hours_daily",
                    e.target.value === "" ? null : e.target.value,
                  )
                }
                className="input"
              />
            </Field>
            <Field label="Contracted hours / week">
              <input
                type="number"
                step="0.5"
                value={form.contracted_hours_weekly ?? ""}
                onChange={(e) =>
                  update(
                    "contracted_hours_weekly",
                    e.target.value === "" ? null : e.target.value,
                  )
                }
                className="input"
              />
            </Field>
          </Row>
          <Row>
            <Field label="Work location">
              <select
                value={form.work_location_type ?? "office"}
                onChange={(e) =>
                  update("work_location_type", e.target.value as "office" | "remote" | "hybrid")
                }
                className="input"
              >
                <option value="office">Office</option>
                <option value="remote">Remote</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </Field>
            {form.work_location_type === "hybrid" && (
              <Field label="Office days">
                <div className="flex flex-wrap gap-2 mt-1">
                  {["monday", "tuesday", "wednesday", "thursday", "friday"].map((day) => {
                    const days: string[] = (form.hybrid_office_days as string[] | undefined) ?? [];
                    const checked = days.includes(day);
                    return (
                      <label key={day} className="flex items-center gap-1 text-xs">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const next = checked
                              ? days.filter((d) => d !== day)
                              : [...days, day];
                            update("hybrid_office_days", next.length ? next : null);
                          }}
                        />
                        {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                      </label>
                    );
                  })}
                </div>
              </Field>
            )}
          </Row>
          <Row>
            <Field label="Break policy">
              <DropdownSelect
                listCode="break_policy"
                value={policy}
                onChange={(v) => update("break_policy", v as BreakPolicy)}
              />
            </Field>
            <Field label="Break duration (mins)">
              <input
                type="number"
                min={0}
                value={form.break_duration_minutes ?? ""}
                onChange={(e) =>
                  update(
                    "break_duration_minutes",
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
                className="input"
              />
            </Field>
          </Row>
          {policy === "fixed" && (
            <Field label="Fixed break start time">
              <input
                type="time"
                value={(form.break_start_time ?? "").slice(0, 5)}
                onChange={(e) =>
                  update(
                    "break_start_time",
                    e.target.value === "" ? null : `${e.target.value}:00`,
                  )
                }
                className="input max-w-xs"
              />
            </Field>
          )}
          {policy === "auto_deduct" && (
            <Field
              label="Auto-deduct threshold (hours)"
              hint="Shifts longer than this automatically deduct the break duration."
            >
              <input
                type="number"
                step="0.5"
                value={form.auto_deduct_threshold_hours ?? ""}
                onChange={(e) =>
                  update(
                    "auto_deduct_threshold_hours",
                    e.target.value === "" ? null : e.target.value,
                  )
                }
                className="input max-w-xs"
              />
            </Field>
          )}
          <Field
            label="Overtime after (daily hours)"
            hint="Overrides the region's default daily overtime threshold."
          >
            <input
              type="number"
              step="0.5"
              value={form.overtime_after_daily_hours ?? ""}
              onChange={(e) =>
                update(
                  "overtime_after_daily_hours",
                  e.target.value === "" ? null : e.target.value,
                )
              }
              className="input max-w-xs"
            />
          </Field>

          <div className="flex items-center justify-between pt-2">
            <div>
              {isEdit && (
                <button
                  type="button"
                  onClick={deactivate}
                  className="rounded-lg border border-red-300 px-3 py-2 text-xs text-red-600"
                >
                  Deactivate
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-lg bg-pme-blue px-5 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {submitting ? "Saving..." : isEdit ? "Save" : "Create"}
              </button>
            </div>
          </div>
        </form>

        {isEdit && department && (
          <section className="border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-900">Break Rules</h4>
              <button
                type="button"
                onClick={() => setAddingRule(true)}
                className="rounded-lg border border-gray-300 px-3 py-1 text-xs"
              >
                + Add Break
              </button>
            </div>
            <div className="mt-3 overflow-x-auto rounded-lg border border-gray-100">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2 text-right">Duration</th>
                    <th className="px-3 py-2">Window</th>
                    <th className="px-3 py-2">Paid?</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {breakRules.map((r) => (
                    <tr key={r.id} className={!r.is_active ? "opacity-50" : ""}>
                      <td className="px-3 py-2 font-medium text-gray-900">
                        {r.name}
                      </td>
                      <td className="px-3 py-2">{formatBreakType(r.break_type)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatMinutes(r.duration_minutes)}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {r.earliest_start_time || r.latest_start_time
                          ? `${r.earliest_start_time?.slice(0, 5) ?? "—"}–${
                              r.latest_start_time?.slice(0, 5) ?? "—"
                            }`
                          : "any time"}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {r.is_paid ? "Paid" : "Unpaid"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => setEditingRuleId(r.id)}
                          className="text-xs text-pme-blue hover:underline"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                  {breakRules.length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-3 py-4 text-center text-xs text-gray-400"
                      >
                        No break rules.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {addingRule && department && (
              <BreakRuleForm
                departmentId={department.id}
                onCancel={() => setAddingRule(false)}
                onSaved={() => {
                  setAddingRule(false);
                  void loadRules();
                }}
              />
            )}
            {editingRuleId != null && (
              <BreakRuleForm
                departmentId={department.id}
                rule={breakRules.find((r) => r.id === editingRuleId)}
                onCancel={() => setEditingRuleId(null)}
                onSaved={() => {
                  setEditingRuleId(null);
                  void loadRules();
                }}
              />
            )}
          </section>
        )}
      </div>
    </div>
  );
}

// ---- Break rule form -------------------------------------------------------

function BreakRuleForm({
  departmentId,
  rule,
  onCancel,
  onSaved,
}: {
  departmentId: number;
  rule?: BreakRule;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!rule;
  const [form, setForm] = useState<Partial<BreakRule>>(
    rule ?? {
      name: "",
      break_type: "other",
      duration_minutes: 15,
      is_paid: false,
      sort_order: 0,
      is_active: true,
    },
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = <K extends keyof BreakRule>(
    key: K,
    value: Partial<BreakRule>[K],
  ) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = { ...form };
      if (isEdit && rule) {
        await api.put(`/api/break-rules/${rule.id}`, payload);
      } else {
        await api.post(`/api/departments/${departmentId}/break-rules`, payload);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const deactivate = async () => {
    if (!rule) return;
    if (!confirm("Deactivate this break rule?")) return;
    await api.delete(`/api/break-rules/${rule.id}`);
    onSaved();
  };

  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Name">
          <input
            required
            value={form.name ?? ""}
            onChange={(e) => update("name", e.target.value)}
            className="input"
          />
        </Field>
        <Field label="Type">
          <DropdownSelect
            listCode="break_type"
            value={(form.break_type ?? "other") as string}
            onChange={(v) => update("break_type", v as BreakType)}
          />
        </Field>
        <Field label="Duration (mins)">
          <input
            type="number"
            min={1}
            value={form.duration_minutes ?? ""}
            onChange={(e) => update("duration_minutes", Number(e.target.value))}
            className="input"
          />
        </Field>
        <Field label="Paid break?">
          <select
            value={form.is_paid ? "true" : "false"}
            onChange={(e) => update("is_paid", e.target.value === "true")}
            className="input"
          >
            <option value="false">Unpaid</option>
            <option value="true">Paid</option>
          </select>
        </Field>
        <Field label="Earliest start">
          <input
            type="time"
            value={(form.earliest_start_time ?? "").slice(0, 5)}
            onChange={(e) =>
              update(
                "earliest_start_time",
                e.target.value === "" ? null : `${e.target.value}:00`,
              )
            }
            className="input"
          />
        </Field>
        <Field label="Latest start">
          <input
            type="time"
            value={(form.latest_start_time ?? "").slice(0, 5)}
            onChange={(e) =>
              update(
                "latest_start_time",
                e.target.value === "" ? null : `${e.target.value}:00`,
              )
            }
            className="input"
          />
        </Field>
        <Field label="Sort order">
          <input
            type="number"
            value={form.sort_order ?? 0}
            onChange={(e) => update("sort_order", Number(e.target.value))}
            className="input"
          />
        </Field>
      </div>

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

      <div className="mt-3 flex items-center justify-between">
        <div>
          {isEdit && (
            <button
              type="button"
              onClick={deactivate}
              className="text-xs text-red-600"
            >
              Deactivate
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-300 px-3 py-1 text-xs"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="rounded bg-pme-blue px-3 py-1 text-xs font-semibold text-white disabled:opacity-60"
          >
            {submitting ? "Saving..." : isEdit ? "Save" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Small helpers ---------------------------------------------------------

function Row({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{children}</div>;
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block text-sm">
      <span className="block font-medium text-gray-700">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    </label>
  );
}
